import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { RefreshCw, TrendingUp, Search, Heart, Flame } from 'lucide-react';

interface TrendingKeywordsProps {
  siteId: string;
  sellerId: number;
  onKeywordClick: (keyword: string) => void;
  externalKeywords?: any[];
}

interface TrendKeyword {
  keyword: string;
  type: 'growing' | 'wanted' | 'popular';
  search_volume?: number;
}

const TYPE_STYLES: Record<string, { bg: string; text: string; icon: typeof TrendingUp }> = {
  growing: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', icon: TrendingUp },
  wanted: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', icon: Search },
  popular: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', icon: Flame },
};

function SkeletonPills() {
  const widths = [80, 120, 96, 140, 72, 110, 88, 130, 100, 116, 92, 124, 84, 108, 136];
  return (
    <div className="flex flex-wrap gap-2">
      {widths.map((w, i) => (
        <div
          key={i}
          className="h-7 rounded-full animate-pulse"
          style={{
            width: `${w}px`,
            backgroundColor: 'var(--color-bg-tertiary)',
          }}
        />
      ))}
    </div>
  );
}

export default function TrendingKeywords({ siteId, sellerId, onKeywordClick, externalKeywords }: TrendingKeywordsProps) {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState<TrendKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchKeywords = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await api.get<{ keywords: TrendKeyword[] }>(
        `/publisher/discover/tendencias/trends?site_id=${siteId}&seller_id=${sellerId}`
      );
      if (res.success && res.data) {
        const arr = Array.isArray(res.data) ? res.data : (res.data as any)?.keywords || [];
        setKeywords(arr.map((k: any) => ({ ...k, type: k.tier || k.type })));
      }
    } catch (err) {
      console.error('Failed to fetch trending keywords', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [siteId, sellerId]);

  useEffect(() => {
    if (externalKeywords) {
      setKeywords(externalKeywords.map((k: any) => ({ ...k, type: k.tier || k.type })));
      setLoading(false);
      return;
    }
    fetchKeywords();
  }, [fetchKeywords, externalKeywords]);

  return (
    <div
      className="rounded-lg border p-4"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            {t('tendencias.trendingKeywords')}
          </h3>
        </div>
        <button
          onClick={() => fetchKeywords(true)}
          disabled={refreshing}
          className="p-1.5 rounded-md hover:opacity-80 transition-opacity disabled:opacity-40"
          style={{ color: 'var(--color-text-tertiary)' }}
          title={t('tendencias.refresh')}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
        {Object.entries(TYPE_STYLES).map(([type, style]) => {
          const Icon = style.icon;
          return (
            <span key={type} className="flex items-center gap-1">
              <Icon className={`w-3 h-3 ${style.text}`} />
              {t(`tendencias.type_${type}`)}
            </span>
          );
        })}
      </div>

      {/* Keywords */}
      {loading ? (
        <SkeletonPills />
      ) : keywords.length === 0 ? (
        <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('tendencias.noKeywords')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {keywords.map((kw, idx) => {
            const style = TYPE_STYLES[kw.type] || TYPE_STYLES.popular;
            const Icon = style.icon;
            return (
              <button
                key={`${kw.keyword}-${idx}`}
                onClick={() => onKeywordClick(kw.keyword)}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                  ${style.bg} ${style.text} hover:opacity-80 transition-opacity cursor-pointer`}
                title={kw.search_volume ? `${kw.search_volume.toLocaleString()} searches` : undefined}
              >
                <Icon className="w-3 h-3" />
                {kw.keyword}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
